<script lang="ts" setup>
import { computed, ref, toRefs } from 'vue';
import type { ContentCollection, ContentReference } from '@ilteducation/content-collections-types';
import useMyCollections from '../use-my-collections';
import useLocale from '../composables/useLocale';
import { useDisplay } from 'vuetify';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { DrawerBottom } from '@ilteducation/components';
import CollectionSelectForm from './CollectionSelectForm.vue';

type Props = {
  contentReferences: ContentReference[];
  context: string;
  showIcons?: boolean;
  includeFavorites?: boolean;
};

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'open-create-collection'): void;
  (e: 'deselect-content'): void;
  (e: 'update-collection', collectionId: string, collectionName: string): void;
}>();
const props = withDefaults(defineProps<Props>(), {
  showIcons: true,
});

const { t } = useLocale();
const { update } = useMyCollections(props.context, 500); // load "all" until we have proper pagination...
const { xs } = useDisplay();

const { contentReferences } = toRefs(props);
const showDialog = ref(true);

const isMobileDevice = computed(() => {
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
  return (
    /android|iphone|ipod|blackberry|bb|playbook|windows phone|webos|opera mini|opera mobi|mobile/i.test(ua) &&
    !/ipad|tablet/i.test(ua)
  );
});

const handleClose = () => {
  emit('close');
};

const handleAbort = () => {
  emit('deselect-content');
  handleClose();
};

const handleCreate = () => {
  emit('open-create-collection');
};

const handleSave = async (selectedCollection: ContentCollection | undefined) => {
  if (selectedCollection && selectedCollection.contents.type === 'ContentsByReferences') {
    const referencesSet = new Set();
    const references: ContentReference[] = [
      ...contentReferences.value,
      ...selectedCollection.contents.references,
    ].filter((r) => {
      const serialisedReference = `${r.type}:${r.id}`;
      const exists = referencesSet.has(serialisedReference);
      referencesSet.add(serialisedReference);

      return !exists;
    });

    await update(selectedCollection.id, {
      contents: {
        type: 'ContentsByReferences',
        references: references,
      },
    }).then(() => {
      emit('update-collection', selectedCollection!.id, selectedCollection!.name);
      emit('deselect-content');
    });
  }
  handleClose();
};
</script>

<template>
  <DrawerBottom
    v-if="xs || isMobileDevice"
    :modelValue="showDialog"
    color="surface-container-lowest"
    @close="handleClose"
    :title="t('content_collections.label_choose_collection')"
    :dialog-label="t('content_collections.label_choose_collection')"
  >
    <CollectionSelectForm
      :content-references="contentReferences"
      :context="context"
      :show-icons="showIcons"
      :include-favorites="includeFavorites"
      @save="handleSave"
      @open-create-collection="handleCreate"
      @abort="handleAbort"
    />
  </DrawerBottom>
  <v-dialog
    v-else
    v-model="showDialog"
    transition="scale-transition"
    scrollable
    :max-width="600"
    :max-height="700"
    @keydown.esc="handleAbort"
    @click:outside="handleAbort"
  >
    <v-card flat color="surface" class="text-on-surface rounded-lg">
      <v-card-title class="py-6 ma-0">
        <slot name="title">
          <h2 class="text-center ma-0">{{ t('content_collections.label_choose_collection') }}</h2>
        </slot>
        <v-btn
          variant="text"
          class="close-btn position-absolute"
          color="secondary"
          @click="handleAbort"
          :aria-label="t('label_close')"
        >
          <FontAwesomeIcon icon="fa-solid fa-xmark" size="lg" />
        </v-btn>
      </v-card-title>
      <CollectionSelectForm
        :content-references="contentReferences"
        :context="context"
        :show-icons="showIcons"
        :include-favorites="includeFavorites"
        @save="handleSave"
        @open-create-collection="handleCreate"
        @abort="handleAbort"
      />
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.close-btn {
  right: 12px;
  top: 12px;
  z-index: 1;
  min-width: unset;
}

:deep(.v-card) {
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
}
</style>
